<template>
  <div class="ws-home">
    <AppNavigation />

    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="gradient-orb orb-1"></div>
      </div>
      
      <v-container class="hero-content py-16">
        <v-row align="center" justify="center">
          <v-col cols="12" md="6" class="text-center text-md-left pr-md-8">
            <div class="hero-eyebrow mb-6" :class="{ 'animate-in': mounted }">
              <v-icon size="16" color="#FF9F6B">mdi-wrench-outline</v-icon>
              <span>Mechanics &middot; Shop Owners &middot; Service Advisors &middot; Customers</span>
            </div>
            
            <h1 class="hero-title mb-6" :class="{ 'slide-in-left': mounted }">
              Run Your Shop. Delight Every Customer. Never Miss a Beat.
            </h1>
            
            <p class="hero-subtitle mb-8" :class="{ 'slide-in-left-delayed': mounted }">
              Wrenzo Suite is the all-in-one platform built just for auto shops — real-time scheduling, direct messaging, digital vehicle histories, and invoicing your customers can pay from their phone, with automated reminders that keep them coming back.
            </p>
            
            <div class="cta-buttons mb-6" :class="{ 'fade-in-up': mounted }">
              <v-btn
                x-large
                depressed
                class="mr-4 mb-3 cta-btn-primary btn-crimson"
                to="/demo"
              >
                <v-icon left size="22" color="#FFFFFF">mdi-rocket-launch</v-icon>
                Get Started Free
              </v-btn>
            </div>
            
            <div class="trust-indicators" :class="{ 'fade-in': mounted }">
              <div class="trust-badge">
                <v-icon color="white" small>mdi-shield-check</v-icon>
                <span>Bank-Level Security</span>
              </div>
              <div class="trust-badge">
                <v-icon color="white" small>mdi-cloud-check</v-icon>
                <span>99.9% Uptime</span>
              </div>
            </div>
          </v-col>
          
          <v-col cols="12" md="6" class="text-center">
            <div class="phone-mockup-container" :class="{ 'float-animation': mounted }">
              <div class="mockup-glow"></div>
              <div class="phone-frame">
                <div class="phone-screen">
                  <!-- Status Bar -->
                  <div class="status-bar">
                    <span class="time">9:41</span>
                    <div class="notch"></div>
                    <div class="status-icons">
                      <v-icon x-small color="white">mdi-signal</v-icon>
                      <v-icon x-small color="white">mdi-wifi</v-icon>
                      <v-icon x-small color="white">mdi-battery</v-icon>
                    </div>
                  </div>
                  
                  <!-- App Header -->
                  <div class="app-header">
                    <v-icon color="white" size="20">mdi-menu</v-icon>
                    <div class="header-logo">
                      <v-icon color="white" size="16">mdi-car-wrench</v-icon>
                      <span>Mike's Auto Repair</span>
                    </div>
                    <div class="header-actions">
                      <v-icon color="white" size="20">mdi-bell</v-icon>
                      <div class="notification-dot"></div>
                    </div>
                  </div>
                  
                  <!-- Content Area -->
                  <div class="screen-content">
                    <!-- Vehicle Card -->
                    <div class="vehicle-card">
                      <div class="vehicle-card-top">
                        <div class="vehicle-avatar">
                          <v-icon color="white" size="20">mdi-car-outline</v-icon>
                        </div>
                        <div class="vehicle-info">
                          <div class="vehicle-name">John's Honda Civic</div>
                          <div class="vehicle-health">
                            <div class="health-dot"></div>
                            <span>Health Score: 92%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Reminder Card -->
                    <div class="reminder-card">
                      <div class="reminder-icon">
                        <v-icon color="white" size="18">mdi-calendar-alert-outline</v-icon>
                      </div>
                      <div class="reminder-text">
                        <div class="reminder-title">Oil Change Due Soon</div>
                        <div class="reminder-sub">Recommended at 5,000 miles</div>
                      </div>
                    </div>

                    <!-- Action Buttons -->
                    <button class="primary-action-btn">
                      <v-icon size="16" color="white">mdi-calendar-check-outline</v-icon>
                      BOOK APPOINTMENT
                    </button>
                    <button class="secondary-action-btn">
                      <v-icon size="16" color="#FF6B7A">mdi-message-text-outline</v-icon>
                      MESSAGE SHOP
                    </button>

                    <!-- Loyalty Card -->
                    <div class="loyalty-card">
                      <div class="loyalty-top">
                        <v-icon color="white" size="18">mdi-star-circle-outline</v-icon>
                        <span>Loyalty Rewards</span>
                      </div>
                      <div class="loyalty-progress-label">7 of 10 visits completed</div>
                      <div class="loyalty-track">
                        <div class="loyalty-fill" style="width: 70%;"></div>
                      </div>
                      <div class="loyalty-footer">3 more visits to a free service!</div>
                    </div>
                  </div>
                  
                  <!-- Bottom Navigation -->
                  <div class="bottom-nav">
                    <div class="nav-item active">
                      <v-icon color="#FF6B7A" size="22">mdi-home</v-icon>
                      <div class="nav-indicator"></div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-car-outline</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-calendar-month</v-icon>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-message</v-icon>
                      <div class="message-badge">2</div>
                    </div>
                    <div class="nav-item">
                      <v-icon size="22">mdi-account-circle</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Features Section -->
    <section id="features" class="features-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': featuresVisible }">
            <v-icon small color="#D6293E">mdi-star-four-points</v-icon>
            <span>ONE PLATFORM FOR YOUR WHOLE SHOP</span>
          </div>
          <h2 class="section-title mb-4" :class="{ 'fade-in-up': featuresVisible }">
            Everything Your Shop Needs
          </h2>
          <p class="section-subtitle" :class="{ 'fade-in-up-delayed': featuresVisible }">
            A platform built for mechanics, admins, and the customers you serve — scheduling, communication, and billing, all in one place
          </p>
        </div>
        
        <v-row class="mb-12">
          <v-col
            v-for="(feature, index) in features"
            :key="index"
            cols="12"
            md="6"
            lg="4"
            class="mb-6"
          >
            <div
              class="feature-card-modern"
              :class="{ 'card-animate-in': featuresVisible }"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
            >
              <div class="feature-card-glow" :style="{ background: feature.gradient }"></div>
              <div class="feature-card-content">
                <div class="feature-icon-modern" :style="{ background: feature.gradient }">
                  <v-icon size="32" color="white">{{ feature.icon }}</v-icon>
                  <div class="icon-ring"></div>
                </div>
                
                <h3 class="feature-title">{{ feature.title }}</h3>
                <p class="feature-description">{{ feature.description }}</p>
                
                <div class="feature-benefits">
                  <div v-for="(benefit, idx) in feature.benefits" :key="idx" class="benefit-item">
                    <v-icon size="14" color="#FF6B7A">mdi-check-circle</v-icon>
                    <span>{{ benefit }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Interactive Demo Section -->
    <section id="demo" class="demo-section">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6" order="2" order-md="1">
            <div class="demo-interface" :class="{ 'slide-in-left': demoVisible }">
              <div class="demo-tabs">
                <div
                  v-for="(tab, index) in demoTabs"
                  :key="index"
                  class="demo-tab"
                  :class="{ active: activeTab === index }"
                  @click="activeTab = index"
                >
                  <v-icon size="20">{{ tab.icon }}</v-icon>
                  <span>{{ tab.label }}</span>
                </div>
              </div>
              
              <div class="demo-content">
                <transition name="fade" mode="out-in">
                  <div :key="activeTab" class="demo-panel">
                    <div class="demo-visual" :style="{ background: demoTabs[activeTab].gradient }">
                      <v-icon size="80" color="white">{{ demoTabs[activeTab].icon }}</v-icon>
                      <div class="demo-float-elements">
                        <div v-for="i in 5" :key="i" class="float-element" :style="getFloatStyle(i)"></div>
                      </div>
                    </div>
                    <h4 class="demo-panel-title">{{ demoTabs[activeTab].title }}</h4>
                    <p class="demo-panel-text">{{ demoTabs[activeTab].description }}</p>
                    
                    <div class="demo-features-list">
                      <div v-for="(item, idx) in demoTabs[activeTab].items" :key="idx" class="demo-feature-item">
                        <div class="feature-number">{{ idx + 1 }}</div>
                        <div class="feature-text">{{ item }}</div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </v-col>
          
          <v-col cols="12" md="6" order="1" order-md="2">
            <div class="demo-info" :class="{ 'slide-in-right': demoVisible }">
              <div class="section-badge mb-4">
                <v-icon small color="#D6293E">mdi-creation</v-icon>
                <span>POWERFUL FEATURES</span>
              </div>
              
              <h2 class="section-title mb-4">
                Designed Around a Real Shop Day
              </h2>
              
              <p class="section-text mb-8">
                Our platform combines an intuitive design with real scheduling and billing power, so mechanics, admins, and customers all actually want to use it every day.
              </p>
              
              <div class="info-stats">
                <div v-for="(stat, index) in platformStats" :key="index" class="stat-item-modern">
                  <div class="stat-icon" :style="{ background: stat.gradient }">
                    <v-icon color="white" size="24">{{ stat.icon }}</v-icon>
                  </div>
                  <div class="stat-details">
                    <div class="stat-value">{{ stat.value }}</div>
                    <div class="stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="benefits-background">
        <div class="benefit-shape shape-1"></div>
        <div class="benefit-shape shape-2"></div>
        <div class="benefit-shape shape-3"></div>
      </div>
      
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': benefitsVisible }">
            <v-icon small color="white">mdi-shield-star</v-icon>
            <span>WHY CHOOSE US</span>
          </div>
          <h2 class="section-title white--text mb-4" :class="{ 'fade-in-up': benefitsVisible }">
            Built to Bring Your Shop and Customers Closer
          </h2>
          <p class="section-subtitle white--text" :class="{ 'fade-in-up-delayed': benefitsVisible }">
            A real communication channel between your shop and every customer — not just a notice on the wall
          </p>
        </div>
        
        <v-row>
          <v-col
            v-for="(benefit, index) in coreBenefits"
            :key="index"
            cols="12"
            md="6"
            class="mb-6"
          >
            <div
              class="benefit-card"
              :class="{ 'card-animate-in': benefitsVisible }"
              :style="{ 'animation-delay': `${index * 0.15}s` }"
            >
              <div class="benefit-card-inner">
                <div class="benefit-icon-section">
                  <div class="benefit-icon-wrap" :style="{ background: benefit.gradient }">
                    <v-icon size="36" color="white">{{ benefit.icon }}</v-icon>
                  </div>
                  <div class="benefit-badge">{{ benefit.badge }}</div>
                </div>
                
                <div class="benefit-content-section">
                  <h3 class="benefit-title">{{ benefit.title }}</h3>
                  <p class="benefit-text">{{ benefit.description }}</p>
                  
                  <div class="benefit-points">
                    <div v-for="(point, idx) in benefit.points" :key="idx" class="point-item">
                      <div class="point-dot"></div>
                      <span>{{ point }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Technology Section -->
    <section class="technology-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4">
            <v-icon small color="#D6293E">mdi-chip</v-icon>
            <span>SECURE INFRASTRUCTURE</span>
          </div>
          <h2 class="section-title mb-4">
            Built on Modern, Secure Infrastructure
          </h2>
          <p class="section-subtitle">
            Customer data and payment security are our top priorities — enterprise-grade from the ground up
          </p>
        </div>
        
        <v-row>
          <v-col
            v-for="(tech, index) in techFeatures"
            :key="index"
            cols="12"
            sm="6"
            md="3"
            class="mb-4"
          >
            <div class="tech-card">
              <div class="tech-icon" :style="{ background: tech.color }">
                <v-icon color="white" size="28">{{ tech.icon }}</v-icon>
              </div>
              <h4 class="tech-title">{{ tech.title }}</h4>
              <p class="tech-description">{{ tech.description }}</p>
            </div>
          </v-col>
        </v-row>
        
        <div class="tech-certifications mt-12">
          <div class="cert-item">
            <v-icon size="32" color="#D6293E">mdi-lock-check</v-icon>
            <span>Encrypted Payments via Stripe</span>
          </div>
          <div class="cert-item">
            <v-icon size="32" color="#D6293E">mdi-shield-check</v-icon>
            <span>Role-Based Security Rules</span>
          </div>
        </div>
      </v-container>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section">
      <v-container>
        <div class="section-header text-center mb-12">
          <div class="section-badge mb-4" :class="{ 'fade-in-up': pricingVisible }">
            <v-icon small color="#D6293E">mdi-currency-usd</v-icon>
            <span>TRANSPARENT PRICING</span>
          </div>
          <h2 class="section-title mb-4" :class="{ 'fade-in-up': pricingVisible }">
            One Flat Plan for Every Shop
          </h2>
          <p class="section-subtitle" :class="{ 'fade-in-up-delayed': pricingVisible }">
            No per-mechanic fees, no tiers to choose — one price, everything included
          </p>
        </div>
        
        <v-row justify="center">
          <v-col cols="12" xl="11">
            <div class="pricing-card-premium" :class="{ 'scale-in': pricingVisible }">
              <div class="pricing-badge">
                <v-icon small color="#FFFFFF">mdi-star</v-icon>
                <span>EVERY SHOP</span>
              </div>

              <div class="pricing-layout">
                <!-- Summary column: price + CTA -->
                <div class="pricing-summary">
                  <div class="pricing-icon">
                    <v-icon size="40" color="white">mdi-car-wrench</v-icon>
                  </div>
                  <h3 class="pricing-plan-name">Wrenzo Suite Platform</h3>
                  <p class="pricing-tagline">Everything your shop needs to run smoother and grow</p>

                  <div class="pricing-amount-container">
                    <div class="pricing-amount-large">
                      <span class="currency">$</span>
                      <span class="amount">150</span>
                    </div>
                    <div class="pricing-period-label">Per Shop / Per Month</div>
                    <p class="pricing-note">Billed monthly • Cancel anytime • Unlike other platforms that charge 3–5x more</p>
                  </div>

                  <v-btn
                    x-large
                    block
                    depressed
                    class="mb-4 btn-crimson"
                    style="text-transform: none; font-size: 16px; padding: 28px;"
                    to="/demo"
                  >
                    <v-icon left size="22" color="#FFFFFF">mdi-calendar-check</v-icon>
                    Schedule Your Demo
                  </v-btn>

                  <div class="pricing-guarantees">
                    <div class="guarantee-item">
                      <v-icon small color="#FF6B7A">mdi-check-decagram</v-icon>
                      <span>Your first month is free</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#FF6B7A">mdi-check-decagram</v-icon>
                      <span>Free setup & onboarding</span>
                    </div>
                    <div class="guarantee-item">
                      <v-icon small color="#FF6B7A">mdi-check-decagram</v-icon>
                      <span>No long-term contracts</span>
                    </div>
                  </div>
                </div>

                <!-- Features column: compact multi-column grid -->
                <div class="pricing-features-panel">
                  <div class="feature-groups">
                    <div v-for="(group, index) in featureGroups" :key="index" class="feature-group">
                      <div class="group-header">
                        <v-icon color="#D6293E" size="18">{{ group.icon }}</v-icon>
                        <h4>{{ group.title }}</h4>
                      </div>
                      <div class="group-features">
                        <div
                          v-for="(feature, idx) in group.features"
                          :key="idx"
                          class="feature-row"
                          :title="feature.detail || ''"
                        >
                          <v-icon color="#FF6B7A" size="16">mdi-check-circle</v-icon>
                          <span class="feature-name">{{ feature.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="final-cta-section">
      <div class="cta-background">
        <div class="cta-orb cta-orb-1"></div>
        <div class="cta-orb cta-orb-2"></div>
        <div class="cta-pattern"></div>
      </div>
      
      <v-container class="text-center">
        <div class="cta-content">
          <v-icon size="64" color="#FF6B7A" class="mb-6">mdi-rocket-launch</v-icon>
          <h2 class="cta-title white--text mb-4">
            Ready to Modernize Your Shop?
          </h2>
          <p class="cta-subtitle mb-8">
            Give your shop one place to schedule, communicate, and bill — and give your customers a reason to keep coming back.
          </p>
          
          <div class="cta-buttons-final">
            <v-btn
              x-large
              depressed
              class="mr-4 mb-3 btn-crimson"
              style="text-transform: none; font-size: 16px; padding: 24px 44px;"
              to="/demo"
            >
              <v-icon left size="22" color="#FFFFFF">mdi-email</v-icon>
              Get Started Now
            </v-btn>
          </div>
        </div>
      </v-container>
    </section>

    <AppFooter />
  </div>
</template>

<script>
  import AppNavigation from '@/components/AppNavigation.vue'
  import AppFooter from '@/components/AppFooter.vue'

  export default {
    name: 'WrenzoHome',
    components: { AppNavigation, AppFooter },
    
    data: () => ({
      mounted: false,
      featuresVisible: false,
      demoVisible: false,
      benefitsVisible: false,
      pricingVisible: false,
      activeTab: 0,
      
      features: [
        {
          icon: 'mdi-calendar-clock-outline',
          gradient: 'linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%)',
          title: 'Smart Scheduling',
          description: 'Mechanics set their own availability, customers book real open slots, and admins can book on anyone\'s behalf — all synced in real time.',
          benefits: [
            'Airbnb-style calendar with live open slots',
            'Customers, mechanics, and admins can all book',
            'Block days off or add slots in seconds'
          ]
        },
        {
          icon: 'mdi-message-text-outline',
          gradient: 'linear-gradient(135deg, #D6293E 0%, #A91324 100%)',
          title: 'Direct Messaging',
          description: 'Customers message their mechanic or the shop directly in the app — unlike other platforms, no missed calls or voicemail tag.',
          benefits: [
            'Real-time chat between customers and staff',
            'Push notifications the moment a message lands',
            'Every conversation kept in one place'
          ]
        },
        {
          icon: 'mdi-car-outline',
          gradient: 'linear-gradient(135deg, #FF9F6B 0%, #C9622E 100%)',
          title: 'Digital Vehicle Garage',
          description: 'A full profile for every vehicle — service history, warning lights, insurance, and an automatic health score mechanics can trust.',
          benefits: [
            'Service history tracked mileage by mileage',
            'Automatic health score & service alerts',
            'Warning lights, insurance & registration on file'
          ]
        },
        {
          icon: 'mdi-receipt-text-outline',
          gradient: 'linear-gradient(135deg, #C89BFF 0%, #6B3FA0 100%)',
          title: 'Invoicing & Payments',
          description: 'Build line-item invoices, print or email them, and let customers pay securely online — or log a cash payment in seconds.',
          benefits: [
            'Line-item invoices with print & PDF export',
            'Secure online payment links via Stripe',
            'Log in-person cash or card payments instantly'
          ]
        },
        {
          icon: 'mdi-email-fast-outline',
          gradient: 'linear-gradient(135deg, #6BD6C4 0%, #17734D 100%)',
          title: 'Automated Reminders',
          description: 'Booking confirmations, day-before reminders, and payment receipts sent automatically — no more manual follow-up calls.',
          benefits: [
            'Instant email confirmation on every booking',
            '24-hour and 2-hour appointment reminders',
            'Automatic receipts the moment payment lands'
          ]
        },
        {
          icon: 'mdi-star-circle-outline',
          gradient: 'linear-gradient(135deg, #FFC46B 0%, #B8860A 100%)',
          title: 'Loyalty & Referrals',
          description: 'Reward repeat customers with loyalty points toward a free service, and reward referrals that bring new customers in the door.',
          benefits: [
            'Points earned automatically per visit',
            'Free service unlocked after 10 visits',
            'Referral tracking and rewards built in'
          ]
        },
        {
          icon: 'mdi-star-outline',
          gradient: 'linear-gradient(135deg, #5FA8FF 0%, #1F4E8C 100%)',
          title: 'Reviews & Ratings',
          description: 'Customers leave real reviews right in the app, and your shop\'s rating updates automatically — visible to every future customer.',
          benefits: [
            'Star ratings and written reviews from real customers',
            'Shop rating updates automatically',
            'Full visibility for admins and mechanics alike'
          ]
        },
        {
          icon: 'mdi-bell-outline',
          gradient: 'linear-gradient(135deg, #FF6B7A 0%, #A91324 100%)',
          title: 'Notifications Built In',
          description: 'A real activity feed for the whole shop — new bookings, cancellations, payments, and reviews land instantly, for the right person.',
          benefits: [
            'Mechanics alerted the moment they get booked',
            'Admins notified the instant a payment lands',
            'Every notification scoped to who needs to see it'
          ]
        },
        {
          icon: 'mdi-shield-check-outline',
          gradient: 'linear-gradient(135deg, #3A3D44 0%, #1F2024 100%)',
          title: 'Admin Command Center',
          description: 'A real dashboard for shop owners — manage staff, vehicles, invoices, and customer relationships from one command center.',
          benefits: [
            'Manage mechanics, customers, and vehicles in one place',
            'Assign customers to a regular mechanic',
            'Role-based security keeps data locked down'
          ]
        }
      ],
      
      demoTabs: [
        {
          icon: 'mdi-cellphone',
          label: 'Customer App',
          title: 'Built for Every Customer',
          description: 'A clean, modern experience for the people who trust your shop with their vehicle — on iOS, Android, or the web.',
          gradient: 'linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%)',
          items: [
            'Book real open appointment slots instantly',
            'Message the shop directly, no phone tag',
            'View full vehicle service history & health score',
            'Push notifications for reminders and replies'
          ]
        },
        {
          icon: 'mdi-account-wrench-outline',
          label: 'Mechanic App',
          title: 'Tools Built for the Bay',
          description: 'Mechanics manage their own calendar, message customers, and see exactly what\'s coming up next.',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
          items: [
            'Set availability and manage bookings in real time',
            'See customer & vehicle history before every job',
            'Message customers directly from the app',
            'Get notified the moment a new job is booked'
          ]
        },
        {
          icon: 'mdi-monitor-dashboard',
          label: 'Admin Dashboard',
          title: 'A Command Center for Owners',
          description: 'Everything a shop owner needs to run the business — staff, customers, invoicing, and reporting in one place.',
          gradient: 'linear-gradient(135deg, #C89BFF 0%, #6B3FA0 100%)',
          items: [
            'Manage every mechanic\'s calendar from one screen',
            'Create invoices and send secure payment links',
            'Track reviews, payments, and shop activity live',
            'Full customer and vehicle database at a glance'
          ]
        },
        {
          icon: 'mdi-store-cog-outline',
          label: 'Shop Branding',
          title: 'Feels Like Your Shop',
          description: 'Your shop\'s name, hours, services, and photos — presented the way you want customers to see you.',
          gradient: 'linear-gradient(135deg, #FFC46B 0%, #B8860A 100%)',
          items: [
            'Shop hours, services, and pricing on display',
            'Upload real photos of your shop and team',
            'Every email and receipt carries your shop\'s name'
          ]
        }
      ],
      
      platformStats: [
        {
          icon: 'mdi-email-fast-outline',
          value: 'Instant',
          label: 'Automated Reminders',
          gradient: 'linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%)'
        },
        {
          icon: 'mdi-car-outline',
          value: 'Unlimited',
          label: 'Vehicles Tracked',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)'
        },
        {
          icon: 'mdi-account-multiple',
          value: 'Unlimited',
          label: 'Customer Accounts',
          gradient: 'linear-gradient(135deg, #FF9F6B 0%, #C9622E 100%)'
        },
        {
          icon: 'mdi-calendar-check-outline',
          value: '24/7',
          label: 'Online Booking',
          gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)'
        }
      ],
      
      coreBenefits: [
        {
          icon: 'mdi-account-heart',
          gradient: 'linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%)',
          badge: 'FOR CUSTOMERS',
          title: 'Book, Message, and Track in One App',
          description: 'Everything about your vehicle and your shop relationship, in one place — no phone tag, no forgotten reminders.',
          points: [
            'Book appointments that actually show real availability',
            'Message your mechanic directly, anytime',
            'See your full service history and vehicle health',
            'Earn loyalty rewards toward a free service'
          ]
        },
        {
          icon: 'mdi-account-wrench',
          gradient: 'linear-gradient(135deg, #6A6D74 0%, #3A3D44 100%)',
          badge: 'FOR MECHANICS',
          title: 'Spend More Time Wrenching, Less on the Phone',
          description: 'Your calendar, your customers, and your messages in one place — instead of juggling a paper schedule and a ringing phone.',
          points: [
            'Manage your own availability and bookings',
            'See vehicle history before the customer arrives',
            'Message customers directly from the app',
            'Get notified instantly on new bookings'
          ]
        },
        {
          icon: 'mdi-office-building-outline',
          gradient: 'linear-gradient(135deg, #FF9F6B 0%, #C9622E 100%)',
          badge: 'FOR SHOP OWNERS',
          title: 'Run the Whole Shop From One Dashboard',
          description: 'Staff, customers, invoicing, and reporting — the tools to run your business without hiring an office manager.',
          points: [
            'Manage every mechanic\'s calendar centrally',
            'Create and send invoices with secure payment links',
            'Log cash and card payments in seconds',
            'Track reviews, ratings, and shop activity live'
          ]
        },
        {
          icon: 'mdi-handshake-outline',
          gradient: 'linear-gradient(135deg, #4A4D54 0%, #2C2E33 100%)',
          badge: 'FOR THE WHOLE SHOP',
          title: 'One Platform, No More Juggling Tools',
          description: 'Replace paper schedules, sticky notes, and scattered spreadsheets with one system everyone actually uses.',
          points: [
            'One shared calendar for every mechanic',
            'One inbox for every customer conversation',
            'One invoicing system, cash or card',
            'Role-based access keeps everyone in their lane'
          ]
        }
      ],
      
      techFeatures: [
        {
          icon: 'mdi-lock-outline',
          color: '#D6293E',
          title: 'Secure Payments',
          description: 'Every online payment runs through Stripe — your shop never touches raw card data.'
        },
        {
          icon: 'mdi-shield-check',
          color: '#A91324',
          title: 'Role-Based Access',
          description: 'Customers, mechanics, and admins each see exactly what\'s relevant to them, and nothing more.'
        },
        {
          icon: 'mdi-cloud-lock',
          color: '#6A6D74',
          title: 'Secure Cloud',
          description: 'Built on Google Firebase with locked-down security rules and a 99.9% uptime guarantee.'
        },
        {
          icon: 'mdi-backup-restore',
          color: '#4A4D54',
          title: 'Automatic Backups',
          description: 'Your customer and vehicle data is backed up automatically — nothing gets lost.'
        },
        {
          icon: 'mdi-update',
          color: '#B8860A',
          title: 'Regular Updates',
          description: 'Continuous improvements and new features, shipped without any work on your end.'
        },
        {
          icon: 'mdi-cellphone-link',
          color: '#8A6B28',
          title: 'Cross-Platform',
          description: 'Works seamlessly across iOS, Android, and any web browser.'
        },
        {
          icon: 'mdi-face-agent',
          color: '#795548',
          title: 'Responsive Support',
          description: 'Our team is on hand to help when you or your customers need it.'
        },
        {
          icon: 'mdi-email-fast-outline',
          color: '#3A3D44',
          title: 'Automated Communication',
          description: 'Booking confirmations, reminders, and receipts sent automatically, on your shop\'s behalf.'
        }
      ],
      
      featureGroups: [
        {
          icon: 'mdi-calendar-clock-outline',
          title: 'Scheduling & Messaging',
          features: [
            { name: 'Real-time availability', detail: 'Mechanics set their own open slots' },
            { name: 'Customer self-booking', detail: 'Book real slots, no phone calls' },
            { name: 'Admin & mechanic booking', detail: 'Book on a customer\'s behalf' },
            { name: 'Direct messaging', detail: 'Customer-to-shop chat, built in' },
            { name: 'Push notifications', detail: 'Instant alerts for messages & bookings' },
            { name: 'Mobile & web apps', detail: 'iOS, Android, and browser access' }
          ]
        },
        {
          icon: 'mdi-car-outline',
          title: 'Garage & Vehicle Health',
          features: [
            { name: 'Full vehicle profiles', detail: 'Make, model, VIN, mileage & more' },
            { name: 'Service history tracking', detail: 'Every service logged by mileage' },
            { name: 'Automatic health score', detail: 'Calculated from real service data' },
            { name: 'Warning light tracking', detail: 'Check engine, ABS, TPMS & more' }
          ]
        },
        {
          icon: 'mdi-receipt-text-outline',
          title: 'Invoicing & Payments',
          features: [
            { name: 'Line-item invoices', detail: 'Build detailed, professional invoices' },
            { name: 'Print & PDF export', detail: 'Hand a printed copy to any customer' },
            { name: 'Secure online payments', detail: 'Stripe-powered payment links' },
            { name: 'Manual payment logging', detail: 'Log cash or card payments instantly' }
          ]
        },
        {
          icon: 'mdi-star-circle-outline',
          title: 'Loyalty & Referrals',
          features: [
            { name: 'Automatic loyalty points', detail: 'Earned on every completed visit' },
            { name: 'Free service rewards', detail: 'Unlocked after 10 loyalty visits' },
            { name: 'Referral tracking', detail: 'Reward customers who refer others' },
            { name: 'Customer reviews', detail: 'Star ratings visible to every customer' }
          ]
        },
        {
          icon: 'mdi-bell-outline',
          title: 'Notifications & Reminders',
          features: [
            { name: 'Booking confirmations', detail: 'Sent automatically on every booking' },
            { name: '24hr & 2hr reminders', detail: 'Cuts down on missed appointments' },
            { name: 'Payment receipts', detail: 'Sent the instant payment is received' },
            { name: 'In-app activity feed', detail: 'Every event, scoped to the right person' }
          ]
        },
        {
          icon: 'mdi-shield-check',
          title: 'Admin & Security',
          features: [
            { name: 'Role-based dashboards', detail: 'Admin, mechanic, and customer views' },
            { name: 'Staff & customer management', detail: 'Full database at a glance' },
            { name: 'Secure cloud infrastructure', detail: 'Locked-down Firebase security rules' },
            { name: 'Technical support', detail: 'Phone, email, and chat support' },
            { name: 'Free onboarding', detail: 'Setup assistance and training' },
            { name: 'Regular updates', detail: 'New features at your request' },
            { name: 'SLA guarantee', detail: '99.9% uptime commitment' }
          ]
        }
      ]
    }),
    
    mounted() {
      setTimeout(() => {
        this.mounted = true;
      }, 100);
      
      this.setupScrollAnimations();
    },
    
    methods: {
      getFloatStyle(index) {
        const positions = [
          { top: '10%', left: '10%' },
          { top: '20%', right: '15%' },
          { bottom: '25%', left: '20%' },
          { bottom: '15%', right: '10%' },
          { top: '50%', left: '50%' }
        ];
        
        return {
          ...positions[index - 1],
          animationDelay: `${index * 0.5}s`
        };
      },
      
      scrollToFeatures() {
        const element = document.getElementById('features');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      
      scrollToDemo() {
        const element = document.getElementById('demo');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
      
      setupScrollAnimations() {
        const observerOptions = {
          threshold: 0.15,
          rootMargin: '0px 0px -50px 0px'
        };
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (entry.target.classList.contains('features-section')) {
                this.featuresVisible = true;
              } else if (entry.target.classList.contains('demo-section')) {
                this.demoVisible = true;
              } else if (entry.target.classList.contains('benefits-section')) {
                this.benefitsVisible = true;
              } else if (entry.target.classList.contains('pricing-section')) {
                this.pricingVisible = true;
              }
            }
          });
        }, observerOptions);
        
        setTimeout(() => {
          const sections = document.querySelectorAll('.features-section, .demo-section, .benefits-section, .pricing-section');
          sections.forEach(section => observer.observe(section));
        }, 500);
      }
    }
  };
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Cormorant+Garamond:wght@400;500;600;700&display=swap');

  * {
    box-sizing: border-box;
  }

  .ws-home {
    --bg-page: #0a0b0e;
    --bg-alt: #101114;
    --bg-card: #15171b;
    --bg-card-hover: #191c21;
    --border-subtle: rgba(255, 255, 255, 0.09);
    --border-crimson: rgba(214, 41, 62, 0.35);
    --text-primary: #F5F6F7;
    --text-secondary: rgba(245, 246, 247, 0.68);
    --text-muted: rgba(245, 246, 247, 0.48);
    --crimson: #D6293E;
    --crimson-light: #FF6B7A;
    --crimson-dark: #A91324;

    overflow-x: hidden;
    background: var(--bg-page);
    padding-top: 80px;
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text-primary);
  }

  .ws-home h1,
  .ws-home h2,
  .ws-home h3,
  .ws-home .hero-title,
  .ws-home .section-title,
  .ws-home .cta-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    letter-spacing: -0.005em;
  }

  .btn-crimson {
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.01em;
    font-size: 14.5px;
    border-radius: 10px !important;
    background: linear-gradient(150deg, var(--crimson-light) 0%, var(--crimson) 60%, var(--crimson-dark) 100%) !important;
    color: #FFFFFF !important;
    box-shadow: 0 6px 18px rgba(214, 41, 62, 0.32) !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .btn-crimson:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(214, 41, 62, 0.42) !important;
  }
  
  .hero-section {
    position: relative;
    min-height: 92vh;
    background: linear-gradient(160deg, #101114 0%, #0a0b0e 55%, #050506 100%);
    display: flex;
    align-items: center;
    overflow: hidden;
    padding-top: 60px;
  }
  
  .hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  }
  
  .gradient-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: 0.3;
  }
  
  .orb-1 {
    width: 560px;
    height: 560px;
    background: radial-gradient(circle, rgba(214, 41, 62, 0.5) 0%, transparent 70%);
    top: -180px;
    right: -160px;
  }
  
  .hero-content {
    position: relative;
    z-index: 2;
    color: white;
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85);
    opacity: 0;
    transform: translateY(-8px);
    transition: all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .hero-eyebrow.animate-in {
    opacity: 1;
    transform: translateY(0);
  }
  
  .hero-title {
    font-size: 3.25rem;
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.02em;
    opacity: 0;
    transform: translateX(-24px);
    transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
  }
  
  .hero-title.slide-in-left {
    opacity: 1;
    transform: translateX(0);
  }
  
  .hero-subtitle {
    font-size: 1.15rem;
    line-height: 1.6;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.72);
    opacity: 0;
    transform: translateX(-24px);
    transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s;
  }
  
  .hero-subtitle.slide-in-left-delayed {
    opacity: 0.95;
    transform: translateX(0);
  }
  
  .cta-buttons {
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.8s ease-out 0.6s;
  }
  
  .cta-buttons.fade-in-up {
    opacity: 1;
    transform: translateY(0);
  }
  
  .cta-btn-primary {
    font-size: 16px;
    padding: 26px 34px !important;
  }
  
  .trust-indicators {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-top: 30px;
    opacity: 0;
    transition: all 0.8s ease-out 0.8s;
  }
  
  .trust-indicators.fade-in {
    opacity: 1;
  }
  
  .trust-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
  }
  
  .phone-mockup-container {
    position: relative;
    display: inline-block;
    opacity: 0;
    transform: translateY(50px);
    transition: all 1s ease-out 0.4s;
  }
  
  .phone-mockup-container.float-animation {
    opacity: 1;
    transform: translateY(0);
    animation: gentle-float 6s infinite ease-in-out;
  }
  
  @keyframes gentle-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }
  
  .mockup-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(214, 41, 62, 0.25) 0%, transparent 70%);
    filter: blur(40px);
    animation: pulse-glow 4s infinite ease-in-out;
  }
  
  @keyframes pulse-glow {
    0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
  }
  
  .phone-frame {
    position: relative;
    width: 340px;
    height: 690px;
    background: #1a1a1a;
    border-radius: 45px;
    padding: 12px;
    box-shadow: 
      0 0 0 2px #2a2a2a,
      0 0 0 8px #1a1a1a,
      0 30px 80px rgba(0, 0, 0, 0.5);
  }
  
  .phone-screen {
    width: 100%;
    height: 100%;
    background: #0e1013;
    border-radius: 35px;
    overflow: hidden;
    position: relative;
  }
  
  .status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    background: linear-gradient(135deg, #D6293E 0%, #A91324 100%);
    color: white;
    font-size: 13px;
    font-weight: 600;
    position: relative;
  }
  
  .time { flex: 1; }
  
  .notch {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 140px;
    height: 22px;
    background: #1a1a1a;
    border-radius: 0 0 20px 20px;
  }
  
  .status-icons {
    display: flex;
    gap: 6px;
    align-items: center;
  }
  
  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: linear-gradient(135deg, #1c1f25 0%, #0e1013 100%);
    color: white;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  
  .header-logo {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 700;
  }
  
  .header-actions { position: relative; }
  
  .notification-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 8px;
    height: 8px;
    background: #D6293E;
    border-radius: 50%;
    border: 2px solid #0e1013;
  }
  
  .screen-content {
    height: calc(100% - 120px);
    overflow-y: auto;
    background: #0e1013;
    padding: 16px;
  }
  
  .screen-content::-webkit-scrollbar { width: 0; }

  .vehicle-card {
    background: #15171b;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 16px;
    padding: 14px;
    margin-bottom: 12px;
  }

  .vehicle-card-top {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .vehicle-avatar {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: linear-gradient(135deg, #D6293E 0%, #A91324 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .vehicle-name {
    font-size: 13.5px;
    font-weight: 700;
    color: #F5F6F7;
  }

  .vehicle-health {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 3px;
  }

  .health-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #6BD6C4;
  }

  .vehicle-health span {
    font-size: 11px;
    color: #868c96;
    font-weight: 600;
  }

  .reminder-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255, 196, 107, 0.1);
    border: 1px solid rgba(255, 196, 107, 0.28);
    border-radius: 14px;
    padding: 12px 14px;
    margin-bottom: 14px;
  }

  .reminder-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: linear-gradient(135deg, #FFC46B 0%, #B8860A 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .reminder-title {
    font-size: 12.5px;
    font-weight: 700;
    color: #F5F6F7;
  }

  .reminder-sub {
    font-size: 10.5px;
    color: #868c96;
    margin-top: 1px;
  }

  .primary-action-btn {
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #D6293E 0%, #A91324 100%);
    color: white;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 10px;
    box-shadow: 0 8px 20px -8px rgba(214, 41, 62, 0.55);
  }

  .secondary-action-btn {
    width: 100%;
    height: 44px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    background: #15171b;
    color: #FF6B7A;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 14px;
  }

  .loyalty-card {
    background: linear-gradient(160deg, #17342b 0%, #15171b 70%);
    border: 1px solid rgba(107, 214, 196, 0.25);
    border-radius: 16px;
    padding: 14px;
  }

  .loyalty-top {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .loyalty-top span {
    font-size: 12.5px;
    font-weight: 700;
    color: #F5F6F7;
  }

  .loyalty-progress-label {
    font-size: 10.5px;
    color: #9aa1ac;
    margin-bottom: 8px;
  }

  .loyalty-track {
    height: 6px;
    border-radius: 3px;
    background: rgba(255,255,255,0.08);
    overflow: hidden;
    margin-bottom: 8px;
  }

  .loyalty-fill {
    height: 100%;
    border-radius: 3px;
    background: linear-gradient(90deg, #29a874, #6BD6C4);
  }

  .loyalty-footer {
    font-size: 10.5px;
    color: #6BD6C4;
    font-weight: 600;
  }
  
  .bottom-nav {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 12px 20px 16px;
    background: #15171b;
    border-top: 1px solid rgba(255,255,255,0.07);
  }
  
  .nav-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: #6b7078;
    transition: all 0.2s ease;
  }
  
  .nav-item.active { color: #FF6B7A; }
  
  .nav-indicator {
    width: 6px;
    height: 6px;
    background: #FF6B7A;
    border-radius: 50%;
    position: absolute;
    bottom: -8px;
  }
  
  .message-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 16px;
    height: 16px;
    background: #D6293E;
    color: white;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #15171b;
  }
  
  .features-section {
    padding: 100px 0 80px;
    background: var(--bg-page);
    position: relative;
  }
  
  .section-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    background: rgba(214, 41, 62, 0.12);
    border: 1px solid var(--border-crimson);
    border-radius: 30px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1px;
    color: var(--crimson-light);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease-out;
  }
  
  .section-badge.fade-in-up {
    opacity: 1;
    transform: translateY(0);
  }
  
  .section-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    line-height: 1.2;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease-out 0.1s;
  }
  
  .section-title.fade-in-up {
    opacity: 1;
    transform: translateY(0);
  }
  
  .section-subtitle {
    font-size: 1.15rem;
    color: var(--text-secondary);
    max-width: 700px;
    margin: 0 auto;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s ease-out 0.2s;
    font-family: 'Manrope', sans-serif;
  }
  
  .section-subtitle.fade-in-up-delayed {
    opacity: 1;
    transform: translateY(0);
  }
  
  .feature-card-modern {
    position: relative;
    background: var(--bg-card);
    border-radius: 18px;
    padding: 40px 32px;
    height: 100%;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .feature-card-modern.card-animate-in {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  
  .feature-card-modern:hover {
    transform: translateY(-6px);
    background: var(--bg-card-hover);
    border-color: var(--border-crimson);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  }
  
  .feature-card-modern:hover .feature-card-glow { opacity: 0.15; }
  
  .feature-card-glow {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  .feature-card-content { position: relative; z-index: 2; }
  
  .feature-icon-modern {
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  .icon-ring {
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    border: 2px solid;
    border-radius: 24px;
    opacity: 0.2;
    border-color: inherit;
    animation: pulse-ring 3s infinite ease-in-out;
  }
  
  @keyframes pulse-ring {
    0%, 100% { transform: scale(1); opacity: 0.2; }
    50% { transform: scale(1.1); opacity: 0.1; }
  }
  
  .feature-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.3;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .feature-description {
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 20px;
    font-family: 'Manrope', sans-serif;
  }
  
  .feature-benefits {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .benefit-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.5;
    font-family: 'Manrope', sans-serif;
  }
  
  .demo-section {
    padding: 100px 0;
    background: var(--bg-alt);
  }
  
  .demo-interface {
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
    opacity: 0;
    transform: translateX(-50px);
    transition: all 0.8s ease-out;
  }
  
  .demo-interface.slide-in-left {
    opacity: 1;
    transform: translateX(0);
  }
  
  .demo-tabs {
    display: flex;
    background: rgba(255, 255, 255, 0.03);
    padding: 8px;
    gap: 8px;
    border-bottom: 1px solid var(--border-subtle);
  }
  
  .demo-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 12px;
    background: transparent;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .demo-tab:hover {
    background: rgba(214, 41, 62, 0.1);
    color: var(--crimson-light);
  }
  
  .demo-tab.active {
    background: rgba(214, 41, 62, 0.14);
    color: var(--crimson-light);
    box-shadow: none;
  }
  
  .demo-content { padding: 40px; }
  
  .demo-panel { animation: fade-in 0.5s ease; }
  
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .demo-visual {
    position: relative;
    height: 200px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    overflow: hidden;
  }
  
  .demo-float-elements {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  .float-element {
    position: absolute;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    animation: float-element 4s infinite ease-in-out;
  }
  
  @keyframes float-element {
    0%, 100% { transform: translateY(0) translateX(0); }
    50% { transform: translateY(-20px) translateX(10px); }
  }
  
  .demo-panel-title {
    font-size: 1.7rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .demo-panel-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: 24px;
    font-family: 'Manrope', sans-serif;
  }
  
  .demo-features-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  
  .demo-feature-item {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }
  
  .feature-number {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%);
    color: #FFFFFF;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
  }
  
  .feature-text {
    flex: 1;
    padding-top: 4px;
    font-size: 1rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Manrope', sans-serif;
  }
  
  .demo-info {
    opacity: 0;
    transform: translateX(50px);
    transition: all 0.8s ease-out;
  }
  
  .demo-info.slide-in-right {
    opacity: 1;
    transform: translateX(0);
  }
  
  .section-text {
    font-size: 1.1rem;
    color: var(--text-secondary);
    line-height: 1.8;
    font-family: 'Manrope', sans-serif;
  }
  
  .info-stats {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
  
  .stat-item-modern {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 24px;
    background: var(--bg-card);
    border-radius: 16px;
    border: 1px solid var(--border-subtle);
    transition: all 0.3s ease;
  }
  
  .stat-item-modern:hover {
    transform: translateY(-4px);
    border-color: var(--border-crimson);
  }
  
  .stat-icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .stat-details { flex: 1; }
  
  .stat-value {
    font-size: 1.8rem;
    font-weight: 800;
    color: var(--text-primary);
    line-height: 1;
    margin-bottom: 6px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .stat-label {
    font-size: 0.95rem;
    color: var(--text-secondary);
    font-weight: 500;
    font-family: 'Manrope', sans-serif;
  }
  
  .benefits-section {
    position: relative;
    padding: 100px 0;
    background: linear-gradient(160deg, #101114 0%, #0a0b0e 100%);
    overflow: hidden;
  }
  
  .benefits-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  
  .benefit-shape {
    position: absolute;
    border-radius: 50%;
    background: rgba(214, 41, 62, 0.07);
    filter: blur(60px);
  }
  
  .shape-1 {
    width: 500px;
    height: 500px;
    top: -100px;
    left: -100px;
    animation: float-shape 20s infinite ease-in-out;
  }
  
  .shape-2 {
    width: 400px;
    height: 400px;
    bottom: -100px;
    right: -100px;
    animation: float-shape 15s infinite ease-in-out 5s;
  }
  
  .shape-3 {
    width: 300px;
    height: 300px;
    top: 50%;
    left: 50%;
    animation: float-shape 18s infinite ease-in-out 10s;
  }
  
  @keyframes float-shape {
    0%, 100% { transform: translate(0, 0); }
    50% { transform: translate(50px, -50px); }
  }
  
  .benefit-card {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 18px;
    padding: 0;
    overflow: hidden;
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .benefit-card.card-animate-in {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  
  .benefit-card:hover {
    transform: translateY(-6px);
    border-color: var(--border-crimson);
  }
  
  .benefit-card-inner { padding: 36px; }
  
  .benefit-icon-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }
  
  .benefit-icon-wrap {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  .benefit-badge {
    padding: 6px 16px;
    background: rgba(214, 41, 62, 0.12);
    border: 1px solid var(--border-crimson);
    color: var(--crimson-light);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
  }
  
  .benefit-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 12px;
    line-height: 1.3;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .benefit-text {
    font-size: 1.05rem;
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 20px;
    font-family: 'Manrope', sans-serif;
  }
  
  .benefit-points {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .point-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Manrope', sans-serif;
  }
  
  .point-dot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    background: linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%);
    border-radius: 50%;
    margin-top: 8px;
  }
  
  .technology-section {
    padding: 100px 0;
    background: var(--bg-alt);
  }
  
  .tech-card {
    text-align: center;
    padding: 32px 20px;
    transition: all 0.3s ease;
  }
  
  .tech-card:hover { transform: translateY(-8px); }
  
  .tech-icon {
    width: 72px;
    height: 72px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }
  
  .tech-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 8px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .tech-description {
    font-size: 0.95rem;
    color: var(--text-secondary);
    line-height: 1.6;
    font-family: 'Manrope', sans-serif;
  }
  
  .tech-certifications {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 32px;
    margin-top: 40px;
    padding: 40px;
    background: var(--bg-card);
    border-radius: 18px;
    border: 1px solid var(--border-subtle);
  }
  
  .cert-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-secondary);
    font-family: 'Manrope', sans-serif;
  }
  
  .pricing-section {
    padding: 100px 0;
    background: var(--bg-page);
  }
  
  .pricing-card-premium {
    position: relative;
    background: var(--bg-card);
    border: 1px solid var(--border-subtle);
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
    opacity: 0;
    transform: scale(0.9);
    transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .pricing-card-premium.scale-in {
    opacity: 1;
    transform: scale(1);
  }
  
  .pricing-badge {
    position: absolute;
    top: 24px;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%);
    color: #FFFFFF;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    box-shadow: 0 4px 12px rgba(214, 41, 62, 0.4);
  }
  
  .pricing-layout {
    display: grid;
    grid-template-columns: 340px 1fr;
  }

  .pricing-summary {
    background: linear-gradient(160deg, #15171b 0%, #0a0b0e 100%);
    padding: 56px 36px;
    text-align: center;
    color: white;
    border-right: 1px solid var(--border-crimson);
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .pricing-icon { margin-bottom: 16px; }
  
  .pricing-plan-name {
    font-size: 1.6rem;
    font-weight: 700;
    margin-bottom: 8px;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .pricing-tagline {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 24px;
    font-family: 'Manrope', sans-serif;
  }

  .pricing-amount-container { margin-bottom: 28px; }
  
  .pricing-amount-large {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    margin-bottom: 6px;
  }
  
  .currency {
    font-size: 2rem;
    font-weight: 700;
    margin-right: 4px;
    margin-top: 6px;
    color: var(--crimson-light);
  }
  
  .amount {
    font-size: 3.75rem;
    font-weight: 800;
    line-height: 1;
    color: var(--crimson-light);
  }

  .pricing-period-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 14px;
    font-family: 'Manrope', sans-serif;
  }
  
  .pricing-note {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    font-family: 'Manrope', sans-serif;
  }
  
  .pricing-features-panel { padding: 48px 40px; }
  
  .feature-groups {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 28px 36px;
  }
  
  .group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-subtle);
  }
  
  .group-header h4 {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0;
    font-family: 'Cormorant Garamond', Georgia, serif;
  }
  
  .group-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .feature-row {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: default;
  }
  
  .feature-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-secondary);
    line-height: 1.3;
    font-family: 'Manrope', sans-serif;
  }
  
  .pricing-guarantees {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.04);
    border-radius: 14px;
    border: 1px solid var(--border-subtle);
    width: 100%;
    margin-top: 8px;
  }
  
  .guarantee-item {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.75);
    font-weight: 500;
    font-family: 'Manrope', sans-serif;
  }
  
  .final-cta-section {
    position: relative;
    padding: 120px 0;
    background: linear-gradient(160deg, #15171b 0%, #0a0b0e 60%, #050506 100%);
    overflow: hidden;
  }
  
  .cta-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  
  .cta-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(214, 41, 62, 0.25) 0%, transparent 70%);
    filter: blur(60px);
    animation: float-orb 15s infinite ease-in-out;
  }
  
  .cta-orb-1 {
    width: 400px;
    height: 400px;
    top: -100px;
    left: -100px;
  }
  
  .cta-orb-2 {
    width: 500px;
    height: 500px;
    bottom: -150px;
    right: -150px;
    animation-delay: 7s;
  }
  
  .cta-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.06) 1px, transparent 1px),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 50px 50px;
    opacity: 0.5;
  }
  
  .cta-content { position: relative; z-index: 2; }
  
  .cta-title {
    font-size: 3rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
  
  .cta-subtitle {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 700px;
    margin: 0 auto 40px;
    line-height: 1.6;
    font-family: 'Manrope', sans-serif;
  }
  
  .cta-buttons-final {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }
  
  @media (max-width: 1264px) {
    .hero-title { font-size: 3rem; }
    .section-title { font-size: 2.5rem; }
    .cta-title { font-size: 3rem; }
  }
  
  @media (max-width: 960px) {
    .pricing-layout { grid-template-columns: 1fr; }
    .pricing-summary { border-right: none; border-bottom: 1px solid var(--border-crimson); }
    .feature-groups { grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }
    .hero-section { min-height: auto; padding: 80px 0 60px; }
    .hero-title { font-size: 2.5rem; text-align: center; }
    .hero-subtitle { font-size: 1.1rem; text-align: center; }
    .cta-buttons { justify-content: center; }
    .trust-indicators { justify-content: center; }
    .phone-frame { width: 280px; height: 570px; }
    .section-title { font-size: 2rem; }
    .section-subtitle { font-size: 1.1rem; }
    .features-section, .demo-section, .benefits-section, .technology-section, .pricing-section { padding: 60px 0; }
    .final-cta-section { padding: 80px 0; }
    .cta-title { font-size: 2.5rem; }
    .cta-subtitle { font-size: 1.2rem; }
    .info-stats { grid-template-columns: 1fr; }
    .demo-tabs { flex-wrap: wrap; }
    .demo-tab { font-size: 12px; padding: 12px 8px; }
    .demo-tab span { display: none; }
  }
  
  @media (max-width: 600px) {
    .hero-title { font-size: 2rem; }
    .hero-subtitle { font-size: 1rem; }
    .cta-btn-primary { font-size: 16px; padding: 20px 32px !important; }
    .trust-indicators { gap: 12px; }
    .trust-badge { font-size: 12px; padding: 6px 12px; }
    .section-title { font-size: 1.75rem; }
    .section-subtitle { font-size: 1rem; }
    .feature-card-modern { padding: 28px 24px; }
    .feature-title { font-size: 1.3rem; }
    .demo-content { padding: 24px; }
    .demo-panel-title { font-size: 1.5rem; }
    .benefit-card-inner { padding: 28px 24px; }
    .benefit-title { font-size: 1.4rem; }
    .pricing-summary { padding: 40px 24px; }
    .pricing-plan-name { font-size: 1.4rem; }
    .amount { font-size: 3rem; }
    .pricing-features-panel { padding: 32px 24px; }
    .feature-groups { grid-template-columns: 1fr; gap: 24px; }
    .cta-title { font-size: 2rem; }
    .cta-subtitle { font-size: 1.1rem; }
    .cta-buttons-final { flex-direction: column; }
  }
  
  .fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
  .fade-enter, .fade-leave-to { opacity: 0; }
  
  ::-webkit-scrollbar { width: 12px; }
  ::-webkit-scrollbar-track { background: #0a0b0e; }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #D6293E 0%, #A91324 100%);
    border-radius: 6px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #FF6B7A 0%, #D6293E 100%);
  }
</style>